import { useState, useEffect } from 'react';
import { Package, DollarSign, ShoppingCart, Clock, Plus, Trash2, Edit, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { getAdminStats, getProducts, getOrders, createProduct, updateProduct, deleteProduct, updateOrderStatus, getCategories } from '../lib/api';
import { toast } from 'sonner';

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const [newProduct, setNewProduct] = useState({
    name: '',
    slug: '',
    description: '',
    price: '',
    original_price: '',
    category_id: '',
    category_name: '',
    images: '',
    stock: 100,
    featured: false,
    deal: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, productsRes, ordersRes, catsRes] = await Promise.all([
        getAdminStats(),
        getProducts({ limit: 100 }),
        getOrders({}),
        getCategories()
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data.products);
      setOrders(ordersRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      const category = categories.find(c => c.id === newProduct.category_id);
      const productData = {
        ...newProduct,
        price: parseFloat(newProduct.price),
        original_price: newProduct.original_price ? parseFloat(newProduct.original_price) : null,
        images: newProduct.images.split(',').map(s => s.trim()).filter(Boolean),
        category_name: category?.name || '',
        slug: newProduct.slug || newProduct.name.toLowerCase().replace(/\s+/g, '-')
      };
      
      if (editingProduct) {
        await updateProduct(editingProduct.id, productData);
        toast.success('Product updated!');
      } else {
        await createProduct(productData);
        toast.success('Product created!');
      }
      
      setProductDialogOpen(false);
      setEditingProduct(null);
      resetProductForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteProduct(productId);
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await updateOrderStatus(orderId, status);
      toast.success('Order status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setNewProduct({
      name: product.name,
      slug: product.slug,
      description: product.description,
      price: product.price.toString(),
      original_price: product.original_price?.toString() || '',
      category_id: product.category_id,
      category_name: product.category_name,
      images: product.images.join(', '),
      stock: product.stock,
      featured: product.featured,
      deal: product.deal
    });
    setProductDialogOpen(true);
  };

  const resetProductForm = () => {
    setNewProduct({
      name: '', slug: '', description: '', price: '', original_price: '',
      category_id: '', category_name: '', images: '', stock: 100, featured: false, deal: false
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#003366] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-page">
      <div className="max-w-[1440px] mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Admin Dashboard</h1>
            <p className="text-slate-500">Manage your store</p>
          </div>
          
          <Dialog open={productDialogOpen} onOpenChange={(open) => {
            setProductDialogOpen(open);
            if (!open) {
              setEditingProduct(null);
              resetProductForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#003366] hover:bg-[#002855]" data-testid="add-product-btn">
                <Plus size={18} className="mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateProduct} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Product Name *</Label>
                    <Input
                      value={newProduct.name}
                      onChange={(e) => setNewProduct(p => ({ ...p, name: e.target.value }))}
                      required
                      data-testid="product-name-input"
                    />
                  </div>
                  <div>
                    <Label>Slug</Label>
                    <Input
                      value={newProduct.slug}
                      onChange={(e) => setNewProduct(p => ({ ...p, slug: e.target.value }))}
                      placeholder="auto-generated"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={newProduct.description}
                    onChange={(e) => setNewProduct(p => ({ ...p, description: e.target.value }))}
                    required
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Price *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newProduct.price}
                      onChange={(e) => setNewProduct(p => ({ ...p, price: e.target.value }))}
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                  <div>
                    <Label>Original Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newProduct.original_price}
                      onChange={(e) => setNewProduct(p => ({ ...p, original_price: e.target.value }))}
                      placeholder="For discounts"
                    />
                  </div>
                  <div>
                    <Label>Stock</Label>
                    <Input
                      type="number"
                      value={newProduct.stock}
                      onChange={(e) => setNewProduct(p => ({ ...p, stock: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </div>

                <div>
                  <Label>Category *</Label>
                  <Select
                    value={newProduct.category_id}
                    onValueChange={(v) => setNewProduct(p => ({ ...p, category_id: v }))}
                  >
                    <SelectTrigger data-testid="product-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Image URLs (comma-separated)</Label>
                  <Input
                    value={newProduct.images}
                    onChange={(e) => setNewProduct(p => ({ ...p, images: e.target.value }))}
                    placeholder="https://example.com/image1.jpg, https://..."
                  />
                </div>

                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newProduct.featured}
                      onCheckedChange={(v) => setNewProduct(p => ({ ...p, featured: v }))}
                    />
                    <Label>Featured</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={newProduct.deal}
                      onCheckedChange={(v) => setNewProduct(p => ({ ...p, deal: v }))}
                    />
                    <Label>Deal</Label>
                  </div>
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setProductDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#003366]" data-testid="save-product-btn">
                    {editingProduct ? 'Update Product' : 'Create Product'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#003366]/10 rounded-lg flex items-center justify-center">
                  <Package className="text-[#003366]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Products</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_products}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#10B981]/10 rounded-lg flex items-center justify-center">
                  <DollarSign className="text-[#10B981]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-slate-900">${stats.total_revenue.toFixed(2)}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#C5A059]/10 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="text-[#C5A059]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total Orders</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_orders}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#FFD700]/10 rounded-lg flex items-center justify-center">
                  <Clock className="text-[#FFD700]" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Pending Orders</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.pending_orders}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            onClick={() => setActiveTab('overview')}
            className={activeTab === 'overview' ? 'bg-[#003366]' : ''}
          >
            Overview
          </Button>
          <Button
            variant={activeTab === 'products' ? 'default' : 'outline'}
            onClick={() => setActiveTab('products')}
            className={activeTab === 'products' ? 'bg-[#003366]' : ''}
            data-testid="products-tab"
          >
            Products ({products.length})
          </Button>
          <Button
            variant={activeTab === 'orders' ? 'default' : 'outline'}
            onClick={() => setActiveTab('orders')}
            className={activeTab === 'orders' ? 'bg-[#003366]' : ''}
            data-testid="orders-tab"
          >
            Orders ({orders.length})
          </Button>
        </div>

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Recent Orders</h3>
              {orders.slice(0, 5).map((order) => (
                <div key={order.id} className="flex justify-between items-center py-3 border-b last:border-0">
                  <div>
                    <p className="font-medium">#{order.id.slice(0, 8)}</p>
                    <p className="text-sm text-slate-500">{order.customer_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">${order.total.toFixed(2)}</p>
                    <p className={`text-sm capitalize ${order.status === 'confirmed' ? 'text-[#10B981]' : 'text-[#C5A059]'}`}>
                      {order.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Top Products</h3>
              {products.filter(p => p.featured).slice(0, 5).map((product) => (
                <div key={product.id} className="flex gap-3 py-3 border-b last:border-0">
                  <img src={product.images?.[0]} alt="" className="w-12 h-12 object-cover rounded" />
                  <div className="flex-1">
                    <p className="font-medium line-clamp-1">{product.name}</p>
                    <p className="text-sm text-[#B12704] font-semibold">${product.price.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="bg-white rounded-lg border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-4 font-semibold text-slate-600">Product</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Category</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Price</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Stock</th>
                    <th className="text-left p-4 font-semibold text-slate-600">Status</th>
                    <th className="text-right p-4 font-semibold text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product) => (
                    <tr key={product.id} className="border-t" data-testid={`admin-product-${product.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={product.images?.[0]} alt="" className="w-10 h-10 object-cover rounded" />
                          <span className="font-medium line-clamp-1">{product.name}</span>
                        </div>
                      </td>
                      <td className="p-4 text-slate-600">{product.category_name}</td>
                      <td className="p-4">
                        <span className="font-semibold text-[#B12704]">${product.price.toFixed(2)}</span>
                        {product.original_price && (
                          <span className="text-slate-400 line-through text-sm ml-2">
                            ${product.original_price.toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="p-4">{product.stock}</td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {product.featured && (
                            <span className="px-2 py-1 bg-[#C5A059]/10 text-[#C5A059] text-xs rounded">Featured</span>
                          )}
                          {product.deal && (
                            <span className="px-2 py-1 bg-[#FFD700]/20 text-slate-700 text-xs rounded">Deal</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(product)}>
                          <Edit size={16} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteProduct(product.id)}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <div className="bg-white rounded-lg border p-8 text-center">
                <p className="text-slate-500">No orders yet</p>
              </div>
            ) : (
              orders.map((order) => (
                <div key={order.id} className="bg-white rounded-lg border overflow-hidden" data-testid={`admin-order-${order.id}`}>
                  <div className="bg-slate-50 px-6 py-4 flex flex-wrap gap-4 justify-between items-center">
                    <div>
                      <p className="font-semibold">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-slate-500">{new Date(order.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Customer</p>
                      <p className="font-medium">{order.customer_name}</p>
                      <p className="text-sm text-slate-500">{order.customer_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total</p>
                      <p className="font-bold text-[#003366]">${order.total.toFixed(2)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Select
                        value={order.status}
                        onValueChange={(v) => handleUpdateOrderStatus(order.id, v)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold mb-3">Items</h4>
                        {order.items.map((item, i) => (
                          <div key={i} className="flex gap-3 mb-2">
                            <img src={item.image} alt="" className="w-12 h-12 object-cover rounded" />
                            <div>
                              <p className="font-medium">{item.name}</p>
                              <p className="text-sm text-slate-500">
                                ${item.price.toFixed(2)} x {item.quantity}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3">Shipping Address</h4>
                        <p className="text-slate-600">
                          {order.shipping_address.address}<br />
                          {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zipCode}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
